import { Box, CardActions, Collapse, List, Typography } from '@mui/material';
import { NewsResponse } from 'src/services/api/response/newsResponse';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from 'react-i18next';
import { NewsCategoryEnum } from 'src/shared/enums/newsCategory.enum';
import LockClockIcon from '@mui/icons-material/LockClock';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { UserTypeEnum } from 'src/shared/enums/userType.enum';
import { useGlobals } from 'src/services/contexts/appContext';
import ReadConfirmButton from './readConfirmButton';
import ActivityButtons from './activityButtons';
import ParticipantsButton from './participantsButton';
import { useFetchNewsById } from '../hooks/queries/useFetchNewsById';
import ViewFile from './viewFile';
import { useMarkAsReadAndUnderstood } from '../hooks/mutations/useMarkAsReadAndUnderstood';
import NewsConfirmedReadersButton from './newsConfirmedReadersButton';

type NewsDetailListItemProps = {
  isActive: boolean;
  newsGuid: string;
};

export default function NewsDetailListItem(props: NewsDetailListItemProps) {
  dayjs.extend(utc);
  const { isActive, newsGuid } = props;
  const { loggedInUser } = useGlobals();

  const { t } = useTranslation(['common', 'news']);
  const [newsItem, setNewsItem] = useState<NewsResponse>();

  const markAsReadAndConfirmed = useMarkAsReadAndUnderstood();

  const fetchNewsItem = useFetchNewsById(newsGuid);

  const markAsConfirmed = async (item: NewsResponse) => {
    markAsReadAndConfirmed.mutate(item.GUID);
  };

  useEffect(() => {
    if (fetchNewsItem.data) {
      setNewsItem(fetchNewsItem.data);
    }
  }, [fetchNewsItem.data]);

  return (
    <>
      <Collapse
        in={isActive}
        timeout={300}
        easing={{
          enter: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
          exit: 'linear',
        }}
        unmountOnExit
      >
        <List sx={{ display: 'flex', flexDirection: 'column', px: 2, mt: 2 }}>
          {newsItem?.PDF && <ViewFile doc={newsItem?.PDF} />}
          {newsItem?.PDFs && newsItem?.PDFs.map((doc) => <ViewFile key={doc} doc={doc} />)}
        </List>
      </Collapse>

      <Collapse
        in={isActive}
        timeout={300}
        easing={{
          enter: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
          exit: 'linear',
        }}
        sx={{ px: 1 }}
      >
        {newsItem && newsItem.ShowConfirmAction && (
          <Box display='flex' justifyContent='space-between'>
            <ReadConfirmButton readStatus={newsItem.ConfirmedByMe} onClick={() => markAsConfirmed(newsItem)} />
            {loggedInUser?.UserType === UserTypeEnum.Admin && newsItem?.Confirmations?.length > 0 && (
              <NewsConfirmedReadersButton items={newsItem?.Confirmations ?? []} />
            )}
          </Box>
        )}
        {newsItem?.Category === NewsCategoryEnum.Activities && (
          <>
            {newsItem?.ActivityDate && (
              <Box display='flex' alignItems='center' flexDirection='row' px={1} mb={1}>
                <CalendarMonthIcon />
                <Typography variant='h6' pl={0.5}>
                  {t('news:activityDate')} {dayjs.utc(newsItem?.ActivityDate).format('dddd')}{' '}
                  {dayjs.utc(newsItem?.ActivityDate).format('DD MMM YYYY')} kl {dayjs.utc(newsItem?.ActivityDate).format('HH:mm')}
                </Typography>
              </Box>
            )}
            {newsItem?.DeadlineDate && (
              <Box display='flex' alignItems='center' flexDirection='row' px={1} mb={1}>
                <LockClockIcon />
                <Typography variant='h6' pl={0.5}>
                  {t('news:deadlineDate')} {dayjs(newsItem?.DeadlineDate).format('YYYY-MM-DD')}
                </Typography>
              </Box>
            )}
          </>
        )}
        <CardActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box>{newsItem?.Category === NewsCategoryEnum.Activities && <ActivityButtons newsItem={newsItem} />}</Box>
          <Box>{newsItem && newsItem?.Participants.length > 0 && <ParticipantsButton items={newsItem?.Participants} />}</Box>
        </CardActions>
      </Collapse>
    </>
  );
}
